import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "reactstrap";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { formatMoney } from "../../../utils/formatMoney";
import { StatusBadge } from "./StatementReport";
import UploadStatementModal from "./UploadStatementModal";
import { can, formatSize, PERMISSIONS, readBlobError, saveBlobResponse, STATUS_META } from "./glUtils";

export default function StatementsTab({ meta, onOpenLedger }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [status, setStatus] = useState("");
  const [openStatementId, setOpenStatementId] = useState(null);

  const params = { page, per_page: perPage, ...(status ? { status } : {}) };
  const statementsQuery = useQuery({
    queryKey: ["glStatements", params],
    queryFn: () => GeneralLedgerService.getStatements(params),
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      query.state.status !== "error" &&
      (query.state.data?.data || []).some((s) => ["uploaded", "parsing"].includes(s.status))
        ? 3000
        : false,
  });

  const invalidate = () =>
    ["glStatements", "glEntries", "glMeta"].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));

  const reparseMutation = useMutation({
    mutationFn: (id) => GeneralLedgerService.reparseStatement(id),
    onSuccess: () => {
      toast.info("Statement queued for re-parsing");
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not re-parse the statement.")),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, force = false }) => GeneralLedgerService.deleteStatement(id, force),
    onSuccess: () => {
      toast.success("Statement and its rows deleted");
      invalidate();
    },
    onError: (e, { id, force }) => {
      const body = e?.response?.data;
      if (!force && e?.response?.status === 409 && body?.code === "overlapping_statements") {
        const names = (body.statements || []).map((s) => `• ${s.original_name}`).join("\n");
        if (window.confirm(`${body.message}\n\n${names}\n\nDelete anyway?`)) {
          deleteMutation.mutate({ id, force: true });
        }
        return;
      }
      toast.error(getApiErrorMessage(e, "Could not delete the statement."));
    },
  });

  const download = async (s) => {
    try {
      saveBlobResponse(await GeneralLedgerService.downloadStatement(s.id), s.original_name);
    } catch (e) {
      toast.error((await readBlobError(e)) || "Download failed.");
    }
  };

  const confirmDelete = (s) => {
    if (window.confirm(`Delete "${s.original_name}" and all ${s.row_count || 0} rows, notes and attachments? This cannot be undone.`)) {
      deleteMutation.mutate({ id: s.id });
    }
  };

  const columns = [
    {
      name: "File",
      grow: 2,
      cell: (s) => (
        <div>
          <div className="gl-cell-strong">{s.original_name}</div>
          <div className="gl-muted">{formatSize(s.size)} · {s.uploaded_by?.name || ""} · {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</div>
        </div>
      ),
    },
    {
      name: "Status",
      width: "180px",
      cell: (s) => (
        <div title={s.error || undefined}>
          <StatusBadge status={s.status} />
          {s.status === "failed" && s.error && <div className="text-danger small mt-1 gl-error-line">{s.error}</div>}
          {s.status === "parsing" && Number.isFinite(s.progress) && <div className="gl-muted small mt-1">{s.progress}% saved</div>}
        </div>
      ),
    },
    {
      name: "Account",
      grow: 1.5,
      cell: (s) =>
        s.bank_profile ? (
          <div>
            <div>{s.bank_profile.label} ****{s.bank_profile.account_last4}</div>
            <div className="gl-muted">{s.company_name}</div>
          </div>
        ) : (
          <span className="gl-muted">Not linked</span>
        ),
    },
    { name: "Period", width: "200px", cell: (s) => (s.period_start ? `${s.period_start} → ${s.period_end}` : "—") },
    { name: "Rows", width: "80px", right: true, selector: (s) => s.row_count ?? 0 },
    { name: "Closing", width: "150px", right: true, cell: (s) => formatMoney(s.closing_balance, s.currency_code) },
    {
      name: "",
      width: "300px",
      right: true,
      cell: (s) => (
        <div className="d-flex gap-1 flex-wrap justify-content-end">
          <Button size="xs" color="light" onClick={() => setOpenStatementId(s.id)}>Details</Button>
          {s.bank_profile_id && (
            <Button size="xs" color="light" onClick={() => onOpenLedger({ statement_id: String(s.id) })}>Rows</Button>
          )}
          <Button size="xs" color="light" onClick={() => download(s)}>PDF</Button>
          {can(meta, PERMISSIONS.UPLOAD) && s.status !== "reconciled" && !["uploaded", "parsing"].includes(s.status) && (
            <Button size="xs" color="light" disabled={reparseMutation.isPending} onClick={() => reparseMutation.mutate(s.id)}>
              Re-parse
            </Button>
          )}
          {can(meta, PERMISSIONS.DELETE) && !["uploaded", "parsing"].includes(s.status) && (
            <Button size="xs" color="danger" outline disabled={deleteMutation.isPending} onClick={() => confirmDelete(s)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
        <Input type="select" style={{ maxWidth: 220 }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([key, m]) => (
            <option key={key} value={key}>{m.label}</option>
          ))}
        </Input>
      </div>
      {statementsQuery.isError && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2">
          <span>{getApiErrorMessage(statementsQuery.error, "Could not load the statements.")}</span>
          <Button size="sm" color="light" onClick={() => statementsQuery.refetch()}>Try again</Button>
        </div>
      )}
      <DataTable
        columns={columns}
        data={statementsQuery.data?.data || []}
        keyField="id"
        progressPending={statementsQuery.isLoading}
        pagination
        paginationServer
        paginationTotalRows={statementsQuery.data?.meta?.total || 0}
        paginationDefaultPage={page}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[25, 50, 100]}
        onChangePage={setPage}
        onChangeRowsPerPage={(size) => { setPerPage(size); setPage(1); }}
        highlightOnHover
        responsive
        noDataComponent={<div className="p-4 gl-muted">No statements uploaded yet.</div>}
      />
      {openStatementId && (
        <UploadStatementModal
          isOpen
          meta={meta}
          initialStatementId={openStatementId}
          toggle={() => setOpenStatementId(null)}
        />
      )}
    </div>
  );
}
